// src/helper/phoneMaskUS.js

// "+1 (" — 4 символа, префикс, который не участвует в нумерации слотов номера
export const PREFIX_LEN = 4;

/**
 * Оставить только цифры.
 * @param {string} s - Входная строка (может содержать любые символы).
 * @returns {string} Только цифры из строки.
 */
export function digitsOnly(s) {
	return (s || '').replace(/\D+/g, '');
}

/**
 * Удалить лидирующую '1', если она попала как код страны.
 * @param {string} d - Строка из цифр.
 * @returns {string} Цифры без начальной '1'.
 */
export function stripLeadingOne(d) {
	if (!d) return '';
	return d[0] === '1' ? d.slice(1) : d;
}

/**
 * Правило NANP: первая цифра Area Code не может быть 0 или 1.
 * Для вставки/автозаполнения — игнорируем такую первую цифру.
 * @param {string} d - Строка из цифр (после удаления кода страны).
 * @returns {string} Цифры без недопустимого первого символа (0/1).
 */
export function stripAreaLeadingZeroOne(d) {
	if (!d) return '';
	const f = d[0];
	if (f === '0' || f === '1') return d.slice(1);
	return d;
}

/**
 * Ограничить до максимум 10 цифр (стандартный NANP local number).
 * @param {string} d - Строка из цифр.
 * @returns {string} До 10 цифр.
 */
export function clamp10(d) {
	return (d || '').slice(0, 10);
}

/**
 * Превращает цифры в формат с маской "+1 (___) ___-____".
 * Подчёркивания заменяются цифрами слева направо.
 * @param {string} digits - До 10 цифр номера (без кода страны).
 * @returns {string} Всегда длиной 17: "+1 (xxx) xxx-xxxx" с '_' на пустых местах.
 */
export function formatUS(digits) {
	const template = '+1 (___) ___-____';
	const arr = template.split('');
	let di = 0;
	for (let i = 0; i < arr.length && di < digits.length; i += 1) {
		if (arr[i] === '_') {
			arr[i] = digits[di];
			di += 1;
		}
	}
	return arr.join('');
}

/**
 * Нормализация произвольного ввода к форматированной строке:
 *   1) вынимаем только цифры
 *   2) удаляем лидирующую 1 (код страны)
 *   3) (новое) игнорируем 0/1 как первую цифру Area Code
 *   4) ограничиваем 10 цифрами
 *   5) форматируем по маске
 * @param {string} raw - Любая входная строка, в т.ч. из paste/autofill.
 * @returns {{formatted: string, digits: string}} Маскированная строка и "чистые" цифры.
 */
export function normalizeToMasked(raw) {
	const only = digitsOnly(raw);
	let d = stripLeadingOne(only);
	d = stripAreaLeadingZeroOne(d); // 👈 запрет 0/1 в первом слоте при вставке/автозаполнении
	d = clamp10(d);
	return { formatted: formatUS(d), digits: d };
}

/**
 * Сколько "цифрослотов" (цифра или '_') слева от каретки, не считая префикс "+1 (".
 * Нужен для корректного позиционирования каретки при редактировании внутри.
 * @param {string} formatted - Строка вида "+1 (___) ___-____".
 * @param {number} caretPos - Позиция каретки (selectionStart).
 * @returns {number} Индекс слота (0..10).
 */
export function inputSlotsBeforeCaret(formatted, caretPos) {
	const s = (formatted || '').slice(0, Math.max(0, caretPos));
	let idx = 0;
	for (let i = PREFIX_LEN; i < s.length; i += 1) {
		if (/\d|_/.test(s[i])) idx += 1;
	}
	return idx;
}

/**
 * Позиция каретки для данного индекса "цифрослота" (0..10), игнорируя префикс.
 * @param {string} formatted - Строка вида "+1 (___) ___-____".
 * @param {number} digitIndex - Индекс слота (сколько цифр "перескочить").
 * @returns {number} Позиция каретки в символах.
 */
export function caretPosFromDigitIndex(formatted, digitIndex) {
	const s = formatted || '';
	let seen = 0;
	for (let i = PREFIX_LEN; i < s.length; i += 1) {
		if (/\d/.test(s[i])) {
			if (seen === digitIndex) return i + 1; // после цифры
			seen += 1;
		} else if (s[i] === '_') {
			if (seen === digitIndex) return i; // на слот
		}
	}
	return s.length;
}

/**
 * Минимальная позиция каретки — после префикса "+1 (".
 * @param {number} pos - Желаемая позиция.
 * @returns {number} Исправленная позиция.
 */
export function clampCaretMinPrefix(pos) {
	return Math.max(PREFIX_LEN, pos);
}
